import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaTrashAlt, FaEdit, FaPlus, FaCheck, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import TipoImovelForm from './TipoImovelForm';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const AddButton = styled.button`
  background: #34574A;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #27443a;
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th, td {
    padding: 16px 12px;
    text-align: left;
    font-size: 0.95rem;
    border-bottom: 1px solid #eee;
  }

  th {
    background: #f8f9fa;
    color: #666;
    font-weight: 600;
    text-transform: uppercase;
  }

  tr:hover {
    background: #f8f9fa;
  }
`;

const StatusButton = styled.button`
  background: ${props => props.ativo ? '#22c55e' : '#c62828'};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 4px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  &:hover {
    opacity: 0.8;
  }
`;

const ActionCell = styled.td`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  color: #c0843d;
  font-size: 1.1rem;
  transition: background 0.2s;
  &:hover {
    background: #f8f8f8;
    color: #c62828;
  }
`;

const TiposImovel = () => {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [pagina, setPagina] = useState(1);
  const tiposPorPagina = 6;
  const totalPaginas = Math.ceil(tipos.length / tiposPorPagina);
  const tiposPaginados = tipos.slice((pagina - 1) * tiposPorPagina, pagina * tiposPorPagina);

  useEffect(() => {
    fetchTipos();
  }, []);

  const fetchTipos = () => {
    setLoading(true);
    fetch(`${import.meta.env.VITE_URL_API}/tiposimovel`, {
      headers: {
        'accept': 'application/json',
      }
    })
      .then(res => res.json())
      .then(data => {
        setTipos(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Erro ao buscar tipos de imóvel.');
        setLoading(false);
      });
  };

  const handleAdd = () => {
    setEditData(null);
    setShowForm(true);
  };

  const handleEdit = (tipo) => {
    setEditData(tipo);
    setShowForm(true);
  };

  const handleDelete = (tipo) => {
    Swal.fire({
      title: 'Tem certeza?',
      text: `Deseja realmente excluir o tipo "${tipo.nomeTIPOIMOVEL}"? Esta ação não poderá ser desfeita!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#c62828',
      cancelButtonColor: '#34574A',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await fetch(`${import.meta.env.VITE_URL_API}/tiposimovel/${tipo.codigoTIPOIMOVEL}`, {
            method: 'DELETE',
            headers: {
              'accept': 'application/json',
            }
          });
          Swal.fire('Excluído!', 'Tipo de imóvel excluído com sucesso.', 'success');
          fetchTipos();
        } catch {
          Swal.fire('Erro', 'Não foi possível excluir o tipo de imóvel.', 'error');
        }
      }
    });
  };

  const handleFormSubmit = async (formData) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const body = JSON.stringify({
      codigoTIPOIMOVEL: editData ? editData.codigoTIPOIMOVEL : undefined,
      nomeTIPOIMOVEL: formData.nome,
      slugTIPOIMOVEL: formData.slug,
      ativoTIPOIMOVEL: formData.ativo === 1 || formData.ativo === true,
    });
    try {
      if (editData) {
        await fetch(`${import.meta.env.VITE_URL_API}/tiposimovel/${editData.codigoTIPOIMOVEL}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'accept': 'application/json',
          },
          body
        });
        Swal.fire('Sucesso', 'Tipo de imóvel atualizado com sucesso!', 'success');
      } else {
        await fetch(`${import.meta.env.VITE_URL_API}/tiposimovel`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'accept': 'application/json',
          },
          body
        });
        Swal.fire('Sucesso', 'Tipo de imóvel criado com sucesso!', 'success');
      }
      setShowForm(false);
      fetchTipos();
    } catch {
      Swal.fire('Erro', 'Não foi possível salvar o tipo de imóvel.', 'error');
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditData(null);
  };

  const handleToggleAtivo = async (tipo) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const novoStatus = Number(tipo.ativoTIPOIMOVEL) === 1 ? 0 : 1;
    try {
      await fetch(`${import.meta.env.VITE_URL_API}/tiposimovel/${tipo.codigoTIPOIMOVEL}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'accept': 'application/json'
        },
        body: JSON.stringify({
          codigoTIPOIMOVEL: tipo.codigoTIPOIMOVEL,
          ativoTIPOIMOVEL: novoStatus
        })
      });
      setTimeout(fetchTipos, 500);
    } catch (err) {
      Swal.fire('Erro', 'Não foi possível atualizar o status.', 'error');
    }
  };

  return (
    <PageContainer>
      <HeaderRow>
        <h1>Tipos de Imóvel</h1>
        <AddButton onClick={handleAdd}>
          <FaPlus /> Novo Tipo
        </AddButton>
      </HeaderRow>
      {showForm && (
        <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.25)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',padding:32,borderRadius:12,minWidth:360}}>
            <h2 style={{marginBottom:16}}>{editData ? 'Editar Tipo' : 'Novo Tipo'}</h2>
            <TipoImovelForm onSubmit={handleFormSubmit} onCancel={handleFormCancel} initialData={editData} />
          </div>
        </div>
      )}
      {loading && <div>Carregando...</div>}
      {error && <div style={{color:'red'}}>{error}</div>}
      {!loading && !error && (
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>Slug</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {tiposPaginados.map(tipo => (
                <tr key={tipo.codigoTIPOIMOVEL}>
                  <td>{tipo.codigoTIPOIMOVEL}</td>
                  <td>{tipo.nomeTIPOIMOVEL}</td>
                  <td>{tipo.slugTIPOIMOVEL}</td>
                  <td>
                    <StatusButton
                      ativo={Number(tipo.ativoTIPOIMOVEL) === 1}
                      onClick={() => handleToggleAtivo(tipo)}
                      title={Number(tipo.ativoTIPOIMOVEL) === 1 ? 'Desativar' : 'Ativar'}
                    >
                      {Number(tipo.ativoTIPOIMOVEL) === 1 ? <FaCheck /> : <FaTimes />} {Number(tipo.ativoTIPOIMOVEL) === 1 ? 'Ativo' : 'Inativo'}
                    </StatusButton>
                  </td>
                  <ActionCell>
                    <IconButton onClick={() => handleEdit(tipo)} title="Editar">
                      <FaEdit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(tipo)} title="Excluir">
                      <FaTrashAlt />
                    </IconButton>
                  </ActionCell>
                </tr>
              ))}
            </tbody>
          </Table>
          {/* Paginação */}
          <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:8,margin:'24px 0'}}>
            <button onClick={()=>setPagina(p=>Math.max(1,p-1))} disabled={pagina===1} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #eee',background:'#fff',cursor:pagina===1?'not-allowed':'pointer'}}>Anterior</button>
            {[...Array(totalPaginas)].map((_,i)=>(
              <button
                key={i}
                onClick={()=>setPagina(i+1)}
                style={{padding:'6px 12px',borderRadius:6,border:'1.5px solid '+(pagina===i+1?'#34574A':'#eee'),background:pagina===i+1?'#34574A':'#fff',color:pagina===i+1?'#fff':'#222',fontWeight:600,cursor:'pointer'}}
              >{i+1}</button>
            ))}
            <button onClick={()=>setPagina(p=>Math.min(totalPaginas,p+1))} disabled={pagina===totalPaginas} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #eee',background:'#fff',cursor:pagina===totalPaginas?'not-allowed':'pointer'}}>Próxima</button>
          </div>
        </TableWrapper>
      )}
    </PageContainer>
  );
};

export default TiposImovel;
